import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * InfoFilled component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconInfoFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 23C14.9174 23 17.7153 21.8411 19.7782 19.7782C21.8411 17.7153 23 14.9174 23 12C23 9.08262 21.8411 6.28473 19.7782 4.22183C17.7153 2.15893 14.9174 1 12 1C9.08262 1 6.28473 2.15893 4.22183 4.22183C2.15893 6.28473 1 9.08262 1 12C1 14.9174 2.15893 17.7153 4.22183 19.7782C6.28473 21.8411 9.08262 23 12 23ZM9.64286 15.7321C9.38238 15.7321 9.13256 15.8356 8.94838 16.0198C8.76419 16.204 8.66071 16.4538 8.66071 16.7143C8.66071 16.9748 8.76419 17.2246 8.94838 17.4088C9.13256 17.593 9.38238 17.6964 9.64286 17.6964H14.3571C14.6176 17.6964 14.8674 17.593 15.0516 17.4088C15.2358 17.2246 15.3393 16.9748 15.3393 16.7143C15.3393 16.4538 15.2358 16.204 15.0516 16.0198C14.8674 15.8356 14.6176 15.7321 14.3571 15.7321H12.9821V11.2143C12.9821 10.9538 12.8787 10.704 12.6945 10.5198C12.5103 10.3356 12.2605 10.2321 12 10.2321H10.4286C10.1681 10.2321 9.91828 10.3356 9.73409 10.5198C9.5499 10.704 9.44643 10.9538 9.44643 11.2143C9.44643 11.4748 9.5499 11.7246 9.73409 11.9088C9.91828 12.093 10.1681 12.1964 10.4286 12.1964H11.0179V15.7321H9.64286ZM13.5714 7.28571C13.5714 7.70248 13.4059 8.10218 13.1112 8.39688C12.8165 8.69158 12.4168 8.85714 12 8.85714C11.5832 8.85714 11.1835 8.69158 10.8888 8.39688C10.5941 8.10218 10.4286 7.70248 10.4286 7.28571C10.4286 6.86895 10.5941 6.46925 10.8888 6.17455C11.1835 5.87985 11.5832 5.71429 12 5.71429C12.4168 5.71429 12.8165 5.87985 13.1112 6.17455C13.4059 6.46925 13.5714 6.86895 13.5714 7.28571Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-info-filled') === undefined) {
  customElements.define('cat-icon-info-filled', BlocksIconInfoFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-info-filled': BlocksIconInfoFilled;
  }
}
